<template>
  <div>
    <v-row>
      <v-col class="py-0" cols="6">
        <v-text-field
          placeholder="bonacasa"
          :label="$t('COMPANIES.ITEM')"
          readonly
        />

        <v-text-field
          v-model="form.person.first_name"
          :counter="255"
          :rules="rules.requiredTextLine"
          :label="`${$t('PROFILE.FIRST_NAME')} *`"
          :error-messages="(errors.person || {}).first_name"
          @input="updateValue('first_name', $event, 'person')"
        />

        <v-text-field
          v-model="form.person.last_name"
          :counter="255"
          :rules="rules.requiredTextLine"
          :label="`${$t('PROFILE.LAST_NAME')} *`"
          :error-messages="(errors.person || {}).last_name"
          @input="updateValue('last_name', $event, 'person')"
        />

        <v-text-field
          v-model="form.user.email"
          :counter="255"
          :rules="rules.requiredEmail"
          :label="`${$t('PROFILE.EMAIL')} *`"
          :error-messages="(errors.user || {}).email"
          @input="updateValue('email', $event, 'user')"
        />
      </v-col>

      <v-col class="py-0" cols="6">
        <div>
          <v-skeleton-loader
            :loading="!form.profile.upload"
            boilerplate
            max-width="365"
            max-height="210"
            type="image"
          >
            <div>
              <v-img
                v-if="form.profile.upload"
                :src="form.profile.upload.url"
                max-width="365"
                max-height="210"
              />
            </div>
          </v-skeleton-loader>

          <autoupload-file-input
            v-model="form.profile.upload"
            :label="$t('ITEM.IMAGE')"
            :url="`uploads/work-profiles`"
            :error-messages="(errors.profile || {}).image"
            prepend-icon="mdi-camera"
            chips
            show-size
            :destroyed="isCreateAction"
            @input="form.profile.upload_id = $event"
            @remove="$emit('removeFile', $event)"
          />
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="py-0" cols="6">
        <v-text-field
          v-model="form.user.username"
          :counter="255"
          :rules="rules.requiredTextLine"
          :label="`${$t('USERS.USERNAME')} *`"
          :error-messages="(errors.user || {}).username"
          @input="updateValue('username', $event, 'user')"
        />

        <v-text-field
          v-model="form.profile.position"
          :rules="rules.textLine"
          :label="$t('PROFILE.POSITION')"
          :error-messages="(errors.profile || {}).position"
          @input="updateValue('position', $event, 'profile')"
        />

        <phone-input
          v-model="form.profile.phone"
          :label="$t('PROFILE.WORK_PHONE')"
          :error-messages="(errors.profile || {}).phone"
          @input="updateValue('phone', $event, 'profile')"
        />
      </v-col>
      <v-col class="py-0" cols="6">
        <v-text-field
          v-if="isCreateAction"
          v-model="form.user.password"
          append-icon="mdi-lock-reset"
          :counter="255"
          :label="`${$t('USERS.PASSWORD')} *`"
          :rules="rules.requiredTextLine"
          :error-messages="(errors.user || {}).password"
          @click:append="generatePassword"
          @input="updateValue('password', $event, 'user')"
        />

        <v-text-field
          v-else
          v-model="form.user.password"
          append-icon="mdi-lock-reset"
          :counter="255"
          :label="`${$t('USERS.NEW_PASSWORD')} *`"
          :error-messages="(errors.user || {}).password"
          @click:append="generatePassword"
          @input="updateValue('password', $event, 'user')"
        />

        <v-select
          v-model="form.role"
          :loading="!!!roles.length"
          :items="roles"
          item-text="title"
          item-value="id"
          multiple
          :rules="rules.requiredSelectArray"
          :label="`${$t('USERS.ROLES')} *`"
          :error-messages="errors.roles"
          @input="updateValue('roles', $event)"
        />

        <phone-input
          v-model="form.profile.mobile_phone"
          :label="$t('PROFILE.WORK_MOBILE_PHONE')"
          :error-messages="(errors.profile || {}).phone"
          @input="updateValue('phone', $event, 'profile')"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-select
          v-model="form.additional_permission"
          :loading="!!!permissions.length"
          :items="permissions"
          item-text="name"
          item-value="id"
          multiple
          label="Permissions"
          :error-messages="errors.additional_permissions"
          @input="updateValue('additional_permissions', $event)"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <h2>{{ $t('PROFILE.WORK_ADDRESS') }}</h2>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="py-0" cols="6">
        <v-text-field
          v-model="form.profile.street"
          :label="$t('PROFILE.STREET')"
          :error-messages="(errors.profile || {}).street"
          @input="updateValue('street', $event, 'profile')"
        />
        <v-text-field
          v-model="form.profile.city"
          :label="$t('PROFILE.CITY')"
          :error-messages="(errors.profile || {}).city"
          @input="updateValue('city', $event, 'profile')"
        />
      </v-col>

      <v-col class="py-0" cols="6">
        <zip-code-input
          v-model="form.profile.zip"
          :readonly="readonly"
          :error-messages="(errors.profile || {}).phone"
          @input="updateValue('zip', $event, 'profile')"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import formEditable, { CREATE } from '@/modules/_global/mixins/form-editable';
import {
  requiredEmail,
  requiredSelectArray,
  requiredTextLine,
  textLine
} from '@/modules/_global/variables/rules';
import AutouploadFileInput from '@/modules/_global/_components/AutouploadFileInput';
import { mask } from 'vue-the-mask';
import { mapActions } from 'vuex';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import ZipCodeInput from '@/modules/_global/_components/zip/ZipCodeInput';
import { PermissionsService } from '@/services/permissions/permissions.service';
import { AboServicesService } from '@/services/abo-services.service';

export default {
  name: 'UserForm',

  directives: {
    mask
  },

  components: {
    ZipCodeInput,
    AutouploadFileInput,
    PhoneInput: () => import('@/modules/_global/_components/phone/PhoneInput')
  },

  mixins: [formEditable, i18nRefreshData],

  data() {
    return {
      form: {
        profile: {
          upload: {}
        },
        role: [],
        user: {},
        person: {},
        additional_permission: []
      },
      rules: {
        requiredTextLine,
        requiredSelectArray,
        requiredEmail,
        textLine
      },
      roles: [],
      permissions: []
    };
  },

  computed: {
    isCreateAction() {
      return this.action === CREATE;
    }
  },

  created() {
    if (this.isCreateAction) {
      this.generatePassword();
    }
    this.refreshData();
  },

  methods: {
    ...mapActions('role', ['retrieveRolesForUsers']),
    refreshData() {
      this.retrieveRolesForUsers().then(({ data }) => {
        this.roles = data;
      });

      PermissionsService.getList({}).then(({ data }) => {
        this.permissions = data.data;
      });
    },
    generatePassword() {
      let length = 8,
        charset =
          'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
        password = '';

      for (let i = 0, n = charset.length; i < length; ++i) {
        password += charset.charAt(Math.floor(Math.random() * n));
      }

      this.$set(this.form.user, 'password', password);
    }
  }
};
</script>

<style scoped></style>
